import { Box, Button, Typography } from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import BottomDrawerMV2 from 'components-v2/atoms/Mobile/BottomDrawer';
import CartPriceFluctuationItem from 'components-v2/mocules/CartPriceFluctuationItem';
import { Modal } from 'components/atoms';
import { FLUCTUATED_TYPE } from 'constants/Enums';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import styles from './styles.module.css';

const VISIBLE_ITEMS = 2;

const FluctuationGroup = ({
  products = [],
  isIncreased = false,
  isMobile = false,
  isQuantityReadOnly = false,
}) => {
  const { t: translateCart } = useTranslation('cart');
  const [isExpanded, setIsExpanded] = useState(false);

  if (products.length === 0) return null;

  const isCollapsible = products.length > VISIBLE_ITEMS;
  const visibleProducts =
    isCollapsible && !isExpanded ? products.slice(0, VISIBLE_ITEMS) : products;
  const groupTitle = isIncreased
    ? translateCart('price_increased_group')
    : translateCart('price_decreased_group');

  return (
    <Box className={styles.group}>
      <Box className={clsx(styles.group_header, isIncreased && styles.group_header_increased)}>
        {groupTitle.replace('{number}', products.length)}
      </Box>
      <Box className={clsx(styles.items, isCollapsible && !isExpanded && styles.items_collapsed)}>
        {visibleProducts.map((product) => (
          <CartPriceFluctuationItem
            key={`fluctuation-${product?.sku}`}
            product={product}
            isMobile={isMobile}
            isQuantityReadOnly={isQuantityReadOnly}
          />
        ))}
      </Box>
      {isCollapsible && (
        <Button
          className={styles.see_all}
          onClick={() => setIsExpanded(!isExpanded)}
          endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          data-test="btn-fluctuation-see-all"
        >
          {isExpanded ? translateCart('price_collapse') : translateCart('price_see_all')}
        </Button>
      )}
    </Box>
  );
};

/**
 * Xác nhận biến động giá, dùng ở 2 chỗ:
 *  - /cart: bấm thanh toán -> xác nhận giá mới rồi mới sang /checkout.
 *  - /checkout: bấm đặt hàng -> xác nhận giá mới rồi đặt hàng luôn (isFromCheckout).
 */
const CartPriceFluctuationModal = ({
  visible,
  onClose,
  onBack,
  onConfirm,
  products = [],
  isMobile = false,
  isFromCheckout = false,
}) => {
  const { t: translateCart } = useTranslation('cart');
  const [isConfirming, setIsConfirming] = useState(false);

  const decreasedProducts = products.filter(
    (item) => item?.fluctuatedType === FLUCTUATED_TYPE.DECREASED,
  );
  const increasedProducts = products.filter(
    (item) => item?.fluctuatedType === FLUCTUATED_TYPE.INCREASED,
  );

  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm();
    setIsConfirming(false);
  };

  const content = (
    <>
      <Box className={styles.header}>
        <Typography className={styles.title} data-test="fluctuation-modal-title">
          {translateCart('price_fluctuation_title').replace('{number}', products.length)}
        </Typography>
      </Box>

      <Box className={styles.body}>
        {/* ở /checkout không cho sửa số lượng ngay trong modal, phải quay lại giỏ hàng */}
        <FluctuationGroup
          products={decreasedProducts}
          isMobile={isMobile}
          isQuantityReadOnly={isFromCheckout}
        />
        <FluctuationGroup
          products={increasedProducts}
          isIncreased
          isMobile={isMobile}
          isQuantityReadOnly={isFromCheckout}
        />
      </Box>

      <Box className={styles.footer}>
        <Typography className={styles.disclaimer}>
          {translateCart('price_fluctuation_disclaimer')}
        </Typography>

        <Box className={styles.actions}>
          <Button
            className={styles.btn_back}
            onClick={onBack || onClose}
            data-test="btn-fluctuation-back"
          >
            {translateCart('back_to_cart')}
          </Button>
          <Button
            className={styles.btn_continue}
            onClick={handleConfirm}
            disabled={isConfirming}
            data-test="btn-fluctuation-continue"
          >
            {isFromCheckout
              ? translateCart('price_fluctuation_checkout')
              : translateCart('price_fluctuation_continue')}
          </Button>
        </Box>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <BottomDrawerMV2
        isShow={visible}
        handleClose={onClose}
        offHeader
        classContainer={styles.drawer_paper}
        classContent={styles.drawer_content}
      >
        {content}
      </BottomDrawerMV2>
    );
  }

  return (
    <Modal open={visible} onClose={onClose}>
      <Box className={styles.wrapper}>{content}</Box>
    </Modal>
  );
};

export default CartPriceFluctuationModal;
